import { apiDelete, apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'

export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERNSHIP' | 'CO_OP'
export type WorkAuthRequired = 'US_CITIZEN' | 'PERMANENT_RESIDENT' | 'OPT' | 'CPT' | 'STEM_OPT' | 'H1B' | 'TN' | 'OTHER' | 'REQUIRES_SPONSORSHIP'
export type JobStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'CLOSED' | 'EXPIRED'
export type ExperienceLevel = 'ENTRY' | 'MID' | 'SENIOR' | 'LEAD' | 'PRINCIPAL' | 'DIRECTOR' | 'EXECUTIVE' | 'OTHER'

export interface JobInstitution {
  id?: string
  logoUrl?: string | null
  name: string
  type?: string
  website?: string
}

export interface JobPoster {
  avatarUrl?: string | null
  companyName?: string
  firstName?: string
  id?: string
  lastName?: string
  title?: string
}

export interface BackendJob {
  applicationCount?: number
  applicationDeadline?: string
  benefits?: string
  createdAt: string
  description: string
  employer?: JobPoster
  employerId: string
  employmentType?: EmploymentType
  experienceLevel?: ExperienceLevel | null
  id: string
  institution?: JobInstitution
  isHybrid?: boolean
  isRemote?: boolean
  isSaved?: boolean
  location?: string
  requirements?: string
  responsibilities?: string
  salaryCurrency?: string
  salaryMax?: number
  salaryMin?: number
  salaryPeriod?: string
  skillsRequired?: string[]
  sponsorshipAvailable?: boolean
  startDate?: string
  status: JobStatus
  title: string
  viewCount?: number
  workAuthRequired?: WorkAuthRequired
}

// Matching v2 — only present when a pair actually cleared the hard gates
// (active accounts, no Contract roles, immigration-aware sponsorship) and
// scored at or above the 70% floor. Below that, the pair simply isn't
// returned at all rather than being padded into the list.
export type MatchTier = 'EXCEPTIONAL' | 'QUALIFIED' | 'STRONG'

export type EmployerSponsorshipPosition = 'DOES_NOT_CURRENTLY_SPONSOR' | 'OPEN_TO_SPONSORSHIP' | 'SPONSORS' | 'UNSURE'

/** Immigration/work-authorization information shown as context alongside a
 * match — never as part of the match score or a reason a pair was excluded.
 * A student's qualifications alone determine `_matchScore`; this tells both
 * sides what the sponsorship timeline looks like so they can have that
 * conversation early, rather than treating an employer's current stance as
 * disqualifying. */
export interface MatchImmigrationContext {
  authorizationThrough?: string | null
  currentlyAuthorized: boolean
  employerSponsorshipPosition: EmployerSponsorshipPosition
  futureSponsorshipNeed: 'LIKELY_NEEDED' | 'NOT_NEEDED' | 'UNKNOWN'
  workAuthorization?: string | null
}

/** Only present on `/jobs/my/recommendations`. `_matchScore` is a 0-100
 * weighted composite of pure candidate/job fit (skills, experience/role fit,
 * education, career interest, location/arrangement, employment type — only
 * pairs scoring ≥70 are returned at all), shown directly as a "X% Match"
 * badge without further derivation. `_matchTier` is the human label for the
 * score band (90+ Exceptional, 80-89 Strong, 70-79 Qualified).
 * `_matchReasons` are the positive factors that drove the match;
 * `_matchConsiderations` are non-exclusionary caveats — never a reason the
 * match was excluded, since anything that would exclude it already didn't
 * make the list. `_matchBreakdown` carries the raw per-factor sub-scores.
 * `_matchImmigrationContext` is a separate feasibility/planning layer, not a
 * scoring input — an employer's sponsorship stance never excludes an
 * otherwise-qualified student. */
export interface RecommendedJob extends BackendJob {
  _matchBreakdown?: Record<string, number>
  _matchConsiderations?: string[]
  _matchImmigrationContext?: MatchImmigrationContext
  _matchReasons?: string[]
  _matchScore?: number
  _matchTier?: MatchTier
}

/** From `/jobs/my/candidate-recommendations` (employers only) - the mirror
 * image of `RecommendedJob`: instead of jobs ranked for one student, this is
 * students ranked against the employer's own active job postings. Each
 * candidate is matched to their single best-fitting job (`jobId`/`jobTitle`),
 * not every job they could fit. */
export interface RecommendedCandidate {
  _matchBreakdown?: Record<string, number>
  _matchConsiderations?: string[]
  _matchImmigrationContext?: MatchImmigrationContext
  _matchReasons?: string[]
  _matchScore: number
  _matchTier?: MatchTier
  avatarUrl?: string | null
  firstName: string
  id: string
  institution?: { logoUrl?: string | null; name: string } | null
  jobId: string
  jobTitle: string
  lastName: string
  major?: string | null
  skills?: string[]
  userId: string
  workAuthorization?: string | null
}

export interface JobListParams {
  employmentType?: string
  experienceLevel?: ExperienceLevel
  limit?: number
  location?: string
  page?: number
  search?: string
}

export interface JobPayload {
  applicationDeadline?: string
  benefits?: string
  description: string
  employmentType?: EmploymentType
  experienceLevel?: ExperienceLevel
  isHybrid?: boolean
  isRemote?: boolean
  location?: string
  requirements?: string
  responsibilities?: string
  salaryMax?: number
  salaryMin?: number
  skillsRequired?: string[]
  sponsorshipAvailable?: boolean
  startDate?: string
  title: string
  workAuthRequired?: WorkAuthRequired
}

export const listJobs = (params?: JobListParams): Promise<PaginatedResult<BackendJob[]>> => apiGetPaginated<BackendJob[]>('/jobs', { params })

export const getJob = (id: string) => apiGet<BackendJob>(`/jobs/${id}`)

export const listSavedJobs = () => apiGet<BackendJob[]>('/jobs/my/saved')

export const listRecommendedJobs = () => apiGet<RecommendedJob[]>('/jobs/my/recommendations')

export const toggleSaveJob = (jobId: string) => apiPost<{ saved: boolean }>(`/jobs/${jobId}/save`)

// Employer-only
export const listMyPostedJobs = (params?: JobListParams): Promise<PaginatedResult<BackendJob[]>> => apiGetPaginated<BackendJob[]>('/jobs/my/posted', { params })

export const listCandidateRecommendations = () => apiGet<RecommendedCandidate[]>('/jobs/my/candidate-recommendations')

export const createJob = (payload: JobPayload) => apiPost<BackendJob>('/jobs', payload)

export const updateJob = (id: string, payload: Partial<JobPayload> & { status?: JobStatus }) => apiPatch<BackendJob>(`/jobs/${id}`, payload)

export const deleteJob = (id: string) => apiDelete<void>(`/jobs/${id}`)

export interface GenerateJobDescriptionPayload {
  additionalContext?: string
  employmentType?: EmploymentType
  jobTitle: string
  location?: string
  skills?: string[]
  sponsorshipAvailable?: boolean
}

/** Response shape isn't documented beyond "200: Job description generated" -
 * unwrap defensively rather than assume a specific field name. */
export const generateJobDescription = async (payload: GenerateJobDescriptionPayload): Promise<string> => {
  const data = await apiPost<Record<string, unknown>>('/ai/generate-job-description', payload)
  const text = data.description ?? data.jobDescription ?? data.text ?? data.content
  return typeof text === 'string' ? text : ''
}
